import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Paginación reutilizable con selector de tamaño (10/25/50/100).
 *
 * Props:
 *  - page:        página actual (1-indexed)
 *  - pageSize:    filas por página
 *  - total:       total de filas
 *  - onPageChange:(page:number) => void
 *  - onPageSizeChange:(size:number) => void
 *  - pageSizes:   array de opciones (default [10,25,50,100])
 *  - testIdPrefix (opcional): prefijo para data-testids
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = [10, 25, 50, 100],
  testIdPrefix = "pagination",
  className = "",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (p - 1) * pageSize + 1;
  const to = Math.min(p * pageSize, total);

  const go = (n) => {
    const target = Math.min(Math.max(1, n), totalPages);
    if (target !== p) onPageChange(target);
  };

  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs flex-wrap ${className}`}
      data-testid={testIdPrefix}
    >
      <div className="flex items-center gap-2 text-gray-600">
        <span>
          Mostrando <span className="font-semibold text-gray-900">{from.toLocaleString("es-EC")}</span>–
          <span className="font-semibold text-gray-900">{to.toLocaleString("es-EC")}</span> de{" "}
          <span className="font-semibold text-gray-900">{total.toLocaleString("es-EC")}</span>
        </span>
        <span className="text-gray-300">·</span>
        <label className="flex items-center gap-1.5">
          <span className="text-gray-500 uppercase tracking-wider font-mono text-[10px]">Filas:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="px-2 py-1 border border-gray-300 rounded bg-white text-xs focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
            data-testid={`${testIdPrefix}-size`}
          >
            {pageSizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => go(1)}
          disabled={p === 1}
          className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Primera"
          data-testid={`${testIdPrefix}-first`}
        >
          <ChevronsLeft size={13} />
        </button>
        <button
          onClick={() => go(p - 1)}
          disabled={p === 1}
          className="px-2.5 py-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft size={12} /> Anterior
        </button>
        <span className="px-3 font-medium text-gray-700" data-testid={`${testIdPrefix}-current`}>
          {p} / {totalPages}
        </span>
        <button
          onClick={() => go(p + 1)}
          disabled={p === totalPages}
          className="px-2.5 py-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
          data-testid={`${testIdPrefix}-next`}
        >
          Siguiente <ChevronRight size={12} />
        </button>
        <button
          onClick={() => go(totalPages)}
          disabled={p === totalPages}
          className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Última"
          data-testid={`${testIdPrefix}-last`}
        >
          <ChevronsRight size={13} />
        </button>
      </div>
    </div>
  );
}

/**
 * Hook helper para paginación client-side.
 * Devuelve { page, setPage, pageSize, setPageSize, paged, total }
 */
import { useState, useMemo, useEffect } from "react";
export function useClientPagination(items, defaultSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultSize);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Auto-clamp: si el total baja (p.ej. por filtro) y estamos en una página inexistente, volvemos a la última válida.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { page, setPage, pageSize, setPageSize, paged, total };
}
